import { RedDotListData, TmapApp } from '@tmap-web-lib/tmap-app-interface'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai/index'
import { debounce } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { gte, lte } from 'semver'

import { globalState } from '@/atoms'
import { sendEventLog } from '@/features/log/log.fn'
import { useOpenService, useOpenServiceUrl } from '@/hooks'
import { GroupListType, ServiceListType } from '@/utils'

import * as styles from './serviceList.css'

interface Props {
  redDotList: Pick<RedDotListData, 'itemGroup' | 'item'>[]
  messageList: RedDotListData[]
}

function ServiceList(props: Props) {
  const { redDotList, messageList } = props
  const globalStateValue = useAtomValue(globalState)
  const env = process.env.NEXT_PUBLIC_TARGET
  // RTG 환경에서는 MY_SERVICE_LIST_RTG key 사용 (square admin에서 RTG 전용으로 분리 관리)
  // 신규 key 미존재 시 기존 MY_SERVICE_LIST 로 fallback
  const serviceList =
    env === 'rtg'
      ? globalStateValue.MY_SERVICE_LIST_NEW_RTG ?? globalStateValue.MY_SERVICE_LIST_NEW
      : globalStateValue.MY_SERVICE_LIST_NEW
  const openService = useOpenService()
  const openServiceUrl = useOpenServiceUrl()

  const os = TmapApp.env.isAndroid ? 'AOS' : 'iOS'

  const callClickDebouncing = useMemo(
    () =>
      debounce(
        (serviceUrl: string, actionLog: string, itemGroup?: string, itemName?: string) => {
          // 로그 전송 및 레드닷 업데이트
          if (itemGroup && itemName) {
            sendEventLog(actionLog, { unit: 'REDDOT' })
            if (
              itemGroup !== 'COMMUNICATION' &&
              itemGroup !== 'HOME' &&
              itemGroup !== 'BOARD' &&
              !itemName.includes('BOARD-')
            ) {
              TmapApp.updateRedDotList({
                updateData: [
                  {
                    itemGroup: itemGroup,
                    item: itemName,
                    displayType: 'REDDOT',
                    readDateTime: dayjs().format('YYYYMMDDHHmmss'),
                  },
                ],
              })
              TmapApp.updateAllRedDots()
            }
          } else {
            sendEventLog(actionLog)
          }

          // 서비스 이동 로직
          if (serviceUrl === 'payment') {
            TmapApp.startPaymentActivity({ url: '/' })
          } else if (serviceUrl.startsWith('tmap://')) {
            openService(serviceUrl)
          } else {
            if (serviceUrl.startsWith('https://')) {
              openServiceUrl(serviceUrl, true)
            } else {
              openServiceUrl(serviceUrl, true, true)
            }
          }
        },
        50,
        { leading: true, trailing: false }
      ),
    [openService, openServiceUrl]
  )

  const handleClick = useCallback(
    (serviceUrl: string, actionLog: string, itemGroup?: string, itemName?: string) => {
      callClickDebouncing(serviceUrl, actionLog, itemGroup, itemName)
    },
    [callClickDebouncing]
  )

  const getServiceListMenu = useCallback(
    (service: GroupListType) => {
      const isRedDot = redDotList.find((redDot) => {
        if (
          (redDot.item.startsWith('EVENT') || redDot.item.startsWith('NOTICE')) &&
          service.redDotItem === 'COMMUNICATION'
        ) {
          return true
        } else {
          if (service.url.startsWith('tmap://board')) {
            const boardid = service.url.split('boardid=')[1]
            if (redDot.item.includes(boardid)) {
              return true
            }
          } else if (
            redDot.item === service.redDotItem ||
            redDot.itemGroup === service.redDotItem
          ) {
            return true
          }
        }
      })
      const message = messageList.map((messageData) => {
        if (messageData.item === service.redDotItem) {
          return messageData.message
        }
      })
      return (
        <button
          data-testid="service_list_name"
          key={service.name}
          className={`${styles.serviceBtn}`}
          onClick={() => {
            if (service.log_action_id) {
              if (isRedDot) {
                handleClick(service.url, service.log_action_id, isRedDot.itemGroup, isRedDot.item)
              } else {
                handleClick(service.url, service.log_action_id)
              }
            }
          }}
        >
          <div
            data-testid={isRedDot && 'service_list_reddot'}
            className={`${styles.reddotWrapper}  ${isRedDot ? styles.redDotVariants.reddot : ''}`}
          >
            {service.name}
          </div>
          {message.length ? <span className={styles.messageReddot}>{message}</span> : null}
        </button>
      )
    },
    [handleClick, redDotList, messageList]
  )

  return (
    <>
      <div className={styles.serviceListWrapper}>
        {serviceList?.map((group: ServiceListType) => {
          return (
            <div key={group.groupCode}>
              <p
                data-testid="service_list_group_name"
                className={styles.groupTitle}
              >
                {group.groupName}
              </p>
              {group.groupList.map((service) => {
                if (!service.osType || service.osType?.includes(os)) {
                  if (
                    (!service.minVersion || gte(TmapApp.env.appVersion, service.minVersion)) &&
                    (!service.maxVersion || lte(TmapApp.env.appVersion, service.maxVersion))
                  ) {
                    return getServiceListMenu(service)
                  }
                }
              })}
            </div>
          )
        })}
      </div>
    </>
  )
}

export { ServiceList }
